import { Component } from 'react';
import { CacheWithLimit } from './CacheWithLimit';
import { findSortOrder, getCacheStartAndEndPages } from './utils';
import { DEFAULT_PER_PAGE } from './consts';
import {
  LazyLoadResults,
  PaginationControllerProps,
  PaginationControllerState,
  SortBy,
  SortById,
} from './types';
import { chunk, isEqual } from 'lodash';
import { SortOrder } from '@ardoq/api-types';

/**
 * Max number of results (components, references, paths) to be stored in cache.
 * Can contain results from multiple cache loads, use SURROUNDING_PAGES_TO_CACHE to control
 * how much data is stored to cache at a time.
 */
const MAX_RESULTS_IN_CACHE = 2000;
const DEFAULT_SORT_ORDER = SortOrder.DEFAULT;
const DEFAULT_SORT_BY_ID = null;

type LazyPaginationControllerProps<TData> = PaginationControllerProps<TData> & {
  totalResults: number;
  loadResults: LazyLoadResults<TData>;
  resultsId: string;
  surroundingPagesToCache: number;
  startPageNumbe?: number;
  onPageNumberChange?: (selectedPage: number) => any;
};
type LazyPaginationControllerState<TData> = PaginationControllerState & {
  dataSource: TData[];
  loadedResultsId?: string;
  isLoading: boolean;
};

/** Similar to PaginationController, but used when each page will be loaded independently.
 * Sorting is also handled via API return requests.
 * In this implementation, page data loading is triggered by prop loadResults, currently a stream action
 */
export class LazyPaginationController<TData> extends Component<
  LazyPaginationControllerProps<TData>,
  LazyPaginationControllerState<TData>
> {
  pageDataCache: CacheWithLimit<TData[]>;
  perPage: number;

  constructor(props: LazyPaginationControllerProps<TData>) {
    super(props);
    const {
      defaultSortOrder = DEFAULT_SORT_ORDER,
      defaultSortById = DEFAULT_SORT_BY_ID,
      perPage,
      dataSource,
      surroundingPagesToCache,
    } = props;
    this.perPage = typeof perPage === 'number' ? perPage : DEFAULT_PER_PAGE;
    // Max size is max results / per page (since each cache item represents 1 page of data),
    // or the max amount of pages that will be loaded into cache at once, whichever is larger.
    this.pageDataCache = new CacheWithLimit<TData[]>(
      Math.max(
        Math.ceil(MAX_RESULTS_IN_CACHE / this.perPage),
        surroundingPagesToCache * 2 + 1
      )
    );
    const currentPageData = this.preloadCache(dataSource, 1);
    this.state = {
      currentPageNumber: props.startPageNumber || 1,
      dataSource: currentPageData,
      sortOrder: defaultSortOrder,
      sortById: defaultSortById,
      isLoading: false,
    };
  }

  /** Determine what range of data to load based on current page and additional metadata
   * E.g. if perPage = 50, currentPage = 1, surroundPagesToCache = 3, we will want to load 200 entries, starting at index 0
   */
  getPageRangeToLoad = (currentPageNumber: number) => {
    const { startPage, endPage } = getCacheStartAndEndPages(
      currentPageNumber,
      this.props.totalResults,
      this.perPage,
      this.props.surroundingPagesToCache
    );
    const startIndex = (startPage - 1) * this.perPage;
    const numEntriesToLoad = Math.min(
      (endPage - startPage + 1) * this.perPage,
      this.props.totalResults - startIndex
    );
    return { pageSize: numEntriesToLoad, startIndex };
  };

  /** Preloads surrounding pages into cache, and returns page data for currentPageNumber */
  preloadCache = (newDataSource: TData[], currentPageNumber: number) => {
    const { startPage } = getCacheStartAndEndPages(
      currentPageNumber,
      this.props.totalResults,
      this.perPage,
      this.props.surroundingPagesToCache
    );
    chunk(newDataSource, this.perPage).forEach((pageData, i) =>
      this.pageDataCache.set(startPage + i, pageData)
    );
    return this.pageDataCache.get(currentPageNumber) || [];
  };

  setPage = (newPageNumber: number) => {
    const cachedDataSource = this.pageDataCache.get(newPageNumber);
    if (cachedDataSource && cachedDataSource.length) {
      this.setState({
        dataSource: cachedDataSource,
        currentPageNumber: newPageNumber,
      });
    } else {
      this.props.loadResults({
        ...this.getPageRangeToLoad(newPageNumber),
        ...(this.state.sortById
          ? { sortById: this.state.sortById, sortBy: this.state.sortOrder }
          : {}),
      });
      this.setState({ currentPageNumber: newPageNumber, isLoading: true });
    }
    this.notifyPageNumberChange(newPageNumber);
  };

  handleSortBy = (sortBy: SortBy<TData>, sortById?: SortById) => {
    const [newSortOrder, newSortById] = findSortOrder(
      this.state,
      sortBy,
      sortById
    );
    const hasNoChange =
      newSortOrder === this.state.sortOrder &&
      newSortById === this.state.sortById;
    if (hasNoChange) return;

    this.pageDataCache.clear();
    const shouldPassSortParams = newSortOrder !== SortOrder.DEFAULT;
    this.props.loadResults({
      ...this.getPageRangeToLoad(1),
      ...(shouldPassSortParams
        ? {
            sortById: newSortById,
            sortBy: newSortOrder,
          }
        : {}),
    });
    this.setState({
      currentPageNumber: 1,
      sortById: newSortById,
      sortOrder: newSortOrder,
      isLoading: true,
    });
    this.notifyPageNumberChange(1);
  };

  componentDidUpdate(
    prevProps: LazyPaginationControllerProps<TData>,
    prevState: LazyPaginationControllerState<TData>
  ) {
    // Due to a really messy architecture, the resultsId is passed here in one update,
    // then the new results are passed in another update
    const newResultsLoading = prevProps.resultsId !== this.props.resultsId;

    const newDataReceived = !isEqual(
      prevProps.dataSource,
      this.props.dataSource
    );

    const loadedResultsHasChanged =
      newDataReceived ||
      (!newResultsLoading &&
        this.props.resultsId !== this.state.loadedResultsId);

    const hasSortingChanged =
      !isEqual(prevState.sortOrder, this.state.sortOrder) ||
      !isEqual(prevState.sortById, this.state.sortById);

    // Cache is invalid once new sorting or query is executed
    if (newResultsLoading || hasSortingChanged) this.pageDataCache.clear();

    if (newResultsLoading) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        currentPageNumber: 1,
        sortById: DEFAULT_SORT_BY_ID,
        sortOrder: DEFAULT_SORT_ORDER,
      });
      this.notifyPageNumberChange(1);
    } else if (loadedResultsHasChanged || hasSortingChanged) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        loadedResultsId: this.props.resultsId,
        dataSource: this.props.dataSource.length
          ? this.preloadCache(
              this.props.dataSource,
              this.state.currentPageNumber
            )
          : [],
        isLoading: false,
      });
    }
  }

  notifyPageNumberChange(pageNumber: number) {
    this.props.onPageNumberChange?.(pageNumber);
  }

  render() {
    const {
      currentPageNumber,
      dataSource: currentPageDataSource,
      sortById,
      sortOrder,
      isLoading,
    } = this.state;

    return this.props.render({
      currentPageNumber,
      perPage: this.perPage,
      totalResults: this.props.totalResults,
      currentPageDataSource,
      onPageSelect: this.setPage,
      sortBy: this.handleSortBy,
      sortById,
      sortOrder,
      isLoading,
    });
  }
}
